import React, { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import DateRangeSelector from "./DateRangeSelector";
import OccupancyGraph from "./graphs/OccupancyGraph";
import "./styles/style.css";
import "./company_admin/company_admin.css";
import MetricsSection from "./new_company_admin/components/MetricsSection";
import PassengerCancellationsTable from "./new_company_admin/components/PassengerCancellationsTable";
import SingleTripsTable from "./new_company_admin/components/SingleTripsTable";

function CompanyAdminDashboardNew({ user, theme_color }) {
  const chartRef = useRef(null);

  const [fetchLoader, setFetchLoader] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [labels, setLabels] = useState([]);
  const [siteData, setSiteData] = useState([]);

  const [singleSearch, setSingleSearch] = useState("");
  const [cancellationSearch, setCancellationSearch] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [tripsData, setTripsData] = useState({});
  const [cancellationsData, setCancellationsData] = useState({
    total_cancellations: 0,
    early: 0,
    late: 0,
    after_cutoff: 0,
    total_cancellations_variance: 0,
    early_variance: 0,
    late_variance: 0,
    after_cutoff_variance: 0,
  });
  const [occupancyData, setOccupancyData] = useState({
    actual_avg_occupancy: 0,
    proposed_avg_occupancy: 0,
    proposed_avg_occupancy_variance: 0,
    actual_avg_occupancy_variance: 0,
  });
  const [costsData, setCostsData] = useState({
    total_costs: 0,
    cost_per_head: 0,
    cost_per_head_actual: 0,
    total_costs_variance: 0,
    cost_per_head_variance: 0,
    cost_per_head_actual: 0,
    cost_per_head_actual_variance: 0,
  });

  const [userCancelData, setUserCancelData] = useState([]);
  const [singleTrips, setSingleTrips] = useState([]);

  // Separate expanded rows per table
  const [cancelExpandedRows, setCancelExpandedRows] = useState([]);
  const [singleExpandedRows, setSingleExpandedRows] = useState([]);

  // Separate sort state per table
  const [cancelSortConfig, setCancelSortConfig] = useState({ key: "", direction: "" });
  const [singleSortConfig, setSingleSortConfig] = useState({ key: "", direction: "" });

  // Pagination state per table
  const [cancelPagination, setCancelPagination] = useState({ current_page: 1, total_pages: 1, total_count: 0, per_page: 25 });
  const [singlePagination, setSinglePagination] = useState({ current_page: 1, total_pages: 1, total_count: 0, per_page: 25 });
  const [cancelPage, setCancelPage] = useState(1);
  const [singlePage, setSinglePage] = useState(1);

  // Debounced search values
  const [debouncedSingleSearch, setDebouncedSingleSearch] = useState("");
  const [debouncedCancelSearch, setDebouncedCancelSearch] = useState("");

  // Debounce search inputs (300ms)
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSingleSearch(singleSearch), 300);
    return () => clearTimeout(handle);
  }, [singleSearch]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedCancelSearch(cancellationSearch), 300);
    return () => clearTimeout(handle);
  }, [cancellationSearch]);

  // Reset page to 1 when search or sort changes
  useEffect(() => { setCancelPage(1); }, [debouncedCancelSearch, cancelSortConfig]);
  useEffect(() => { setSinglePage(1); }, [debouncedSingleSearch, singleSortConfig]);

  const handleCancelRowClick = (id) => {
    setCancelExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSingleRowClick = (id) => {
    setSingleExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleCancelSort = (key) => {
    let direction = "asc";
    if (cancelSortConfig.key === key && cancelSortConfig.direction === "asc") {
      direction = "desc";
    }
    setCancelSortConfig({ key, direction });
  };

  const handleSingleSort = (key) => {
    let direction = "asc";
    if (singleSortConfig.key === key && singleSortConfig.direction === "asc") {
      direction = "desc";
    }
    setSingleSortConfig({ key, direction });
  };

  const qs = () => {
    const p = new URLSearchParams();
    if (startDate) p.set("start_date", startDate);
    if (endDate) p.set("end_date", endDate);
    return p.toString() ? `?${p.toString()}` : "";
  };

  const fetchSingleTrips = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (debouncedSingleSearch) params.set("search", debouncedSingleSearch);
      if (singleSortConfig.key) {
        params.set("sort_by", singleSortConfig.key);
        params.set("sort_dir", singleSortConfig.direction);
      }
      params.set("page", String(singlePage));
      params.set("per_page", "25");

      const { data } = await axios.get(
        `/dashboard/site_single_trips?${params.toString()}`,
        { withCredentials: true }
      );

      setSingleTrips(
        (data.data || []).map((item) => ({
          name: item.name,
          total_trips: item.total_trips,
          total_cost: item.total_cost,
          trips: item.trips.map((t) => ({
            date: t.date,
            cost: t.cost,
          })),
        }))
      );
      if (data.meta) setSinglePagination(data.meta);
    } catch (e) {
      console.error("Failed to fetch single trips", e);
    }
  }, [startDate, endDate, debouncedSingleSearch, singleSortConfig, singlePage]);

  const fetchUserCancellations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (debouncedCancelSearch) params.set("search", debouncedCancelSearch);
      if (cancelSortConfig.key) {
        params.set("sort_by", cancelSortConfig.key);
        params.set("sort_dir", cancelSortConfig.direction);
      }
      params.set("page", String(cancelPage));
      params.set("per_page", "25");

      const { data } = await axios.get(
        `/dashboard/site_user_cancellations?${params.toString()}`,
        { withCredentials: true }
      );
      setUserCancelData(
        (data.data || []).map((item) => ({
          name: item.name,
          total_cancellations: item.total_cancellations,
          total_sum: item.total_sum,
          trips: item.trips.map((t) => ({
            cancelled_by: t.cancelled_by,
            cancelled_at: t.cancelled_at,
            passenger_fare: t.passenger_fare,
          })),
        }))
      );
      if (data.meta) setCancelPagination(data.meta);
    } catch (e) {
      console.error("Failed to fetch user cancellations", e);
    }
  }, [startDate, endDate, debouncedCancelSearch, cancelSortConfig, cancelPage]);

  const fetchAll = async () => {
    setFetchLoader(true);
    setFetchError(null);
    try {
      const [tripsRes, cancRes, occRes, costRes, trendRes] = await Promise.all([
        axios.get(`/dashboard/site_trips${qs()}`),
        axios.get(`/dashboard/site_cancellations${qs()}`),
        axios.get(`/dashboard/site_occupancy${qs()}`),
        axios.get(`/dashboard/site_costs${qs()}`),
        axios.get(`/dashboard/site_occupancy_trends${qs()}`),
      ]);

      setTripsData(tripsRes.data);
      setCancellationsData(cancRes.data);
      setOccupancyData(occRes.data);
      setCostsData(costRes.data);

      // Graph data parsing
      const trendData = trendRes?.data;
      if (trendData && Object.keys(trendData).length > 0) {
        const companyNames = Object.keys(trendData);
        if (companyNames.length === 0) {
          setLabels([]);
          setSiteData([]);
          return;
        }

        const firstCompany = trendData[companyNames[0]];
        const weeks =
          Object.keys(firstCompany.occupancy || {}).length > 0
            ? Object.keys(firstCompany.occupancy)
            : Object.keys(
                firstCompany.occupancy_actual ||
                  firstCompany.cost_per_head ||
                  firstCompany.cost_per_head_actual ||
                  {}
              );

        const company = companyNames.map((name) => {
          const {
            primary_color,
            secondary_color,
            occupancy,
            occupancy_actual,
            cost_per_head,
            cost_per_head_actual,
          } = trendData[name];

          return {
            name,
            barData: weeks.map((w) => occupancy?.[w] ?? 0),
            lineData: weeks.map((w) => cost_per_head?.[w] ?? 0),
            bar2Data: weeks.map((w) => occupancy_actual?.[w] ?? 0),
            line2Data: weeks.map((w) => cost_per_head_actual?.[w] ?? 0),
            barBackgroundColor: primary_color,
            barBorderColor: primary_color,
            bar2BackgroundColor: secondary_color,
            bar2BorderColor: secondary_color,
            lineBorderColor: primary_color,
            line2BorderColor: secondary_color,
          };
        });

        setLabels(weeks);
        setSiteData(company);
      }
    } catch (e) {
      console.error("Dashboard fetch error", e);
      setFetchError("Failed to load dashboard data. Please try again.");
    } finally {
      setFetchLoader(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [startDate, endDate]);

  useEffect(() => {
    fetchSingleTrips();
  }, [fetchSingleTrips]);

  useEffect(() => {
    fetchUserCancellations();
  }, [fetchUserCancellations]);

  const tripMetrics = [
    {
      label: "Trips",
      value: tripsData.total_grouped_trips,
      variance: tripsData.total_grouped_trips_variance,
    },
    {
      label: "Single Trips",
      value: tripsData.single_trips,
      variance: tripsData.single_trips_variance,
    },
    {
      label: "Adhocs",
      value: tripsData.adhoc_trips,
      variance: tripsData.adhoc_trips_variance,
    },
    {
      label: "EE No.",
      value: tripsData.unique_users,
      variance: tripsData.unique_users_variance,
    },
  ];

  const cancelMetrics = [
    {
      label: "Total",
      value: cancellationsData.total_cancellations,
      variance: cancellationsData.total_cancellations_variance,
    },
    {
      label: "Early",
      value: cancellationsData.early,
      variance: cancellationsData.early_variance,
    },
    {
      label: "Late",
      value: cancellationsData.late,
      variance: cancellationsData.late_variance,
    },
  ];

  const occupancyMetrics = [
    {
      label: "Proposed",
      value: occupancyData.proposed_avg_occupancy,
      variance: occupancyData.proposed_avg_occupancy_variance,
    },
    {
      label: "Actual",
      value: occupancyData.actual_avg_occupancy,
      variance: occupancyData.actual_avg_occupancy_variance,
    },
  ];
  const passengerMetrics = [
    {
      label: "Proposed",
      value: costsData.cost_per_head,
      variance: costsData.cost_per_head_variance,
    },
    {
      label: "Actual",
      value: costsData.cost_per_head_actual,
      variance: costsData.cost_per_head_actual_variance,
    },
  ];

  return (
    <div className="w-full flex flex-col space-y-4">
      {/* Section 1: Page Title and Date selector */}
      <div className="flex justify-between items-center w-full bg-transparent">
        <div className="flex flex-col">
          <div className="heading flex flex-row">
            <h1 style={{ color: "#908C90" }}>Company Admin Dashboard</h1>
            <button className="text-gray-500 cursor-pointer hover:text-gray-900 ml-2">
              <i className="fas fa-question-circle" />
            </button>
          </div>
          <p className="text-sm font-thin" style={{ color: "#6E6C71" }}>
            Logged in as {user.name} {user.surname}
          </p>
        </div>

        <div className="flex flex-col w-1/3">
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(from, to) => {
              setStartDate(from);
              setEndDate(to);
            }}
          />
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex justify-between items-center">
          <span>{fetchError}</span>
          <button
            onClick={fetchAll}
            className="ml-4 bg-red-700 text-white px-3 py-1 rounded text-sm hover:bg-red-800"
          >
            Retry
          </button>
        </div>
      )}

      {fetchLoader ? (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-50 overflow-hidden bg-gray-700 opacity-50 flex flex-col items-center justify-center">
          <div className="loader ease-linear rounded-full border-4 border-t-4 h-12 w-12 mb-4"></div>
        </div>
      ) : (
        <>
          {/* Section 2: Data aggregators */}
          <div
            className="w-full space-x-4 justify-around items-center flex flex-row my-1"
            style={{ height: "8.5rem" }}
          >
            <MetricsSection
              themeColor={theme_color}
              tripMetrics={tripMetrics}
              cancelMetrics={cancelMetrics}
              passengerMetrics={passengerMetrics}
              occupancyMetrics={occupancyMetrics}
              costsData={costsData}
            />
          </div>

          {/* Section 3: Tables */}

          <div className="w-full space-x-2 justify-between items-center flex">
            {/* Table 1 */}
            <PassengerCancellationsTable
              themeColor={theme_color}
              cancellationSearch={cancellationSearch}
              setCancellationSearch={setCancellationSearch}
              handleSort={handleCancelSort}
              userCancelData={userCancelData}
              handleRowClick={handleCancelRowClick}
              expandedRows={cancelExpandedRows}
              sortConfig={cancelSortConfig}
              pagination={cancelPagination}
              onPageChange={setCancelPage}
            />

            {/* Table 2 */}
            <SingleTripsTable
              themeColor={theme_color}
              singleSearch={singleSearch}
              setSingleSearch={setSingleSearch}
              handleSort={handleSingleSort}
              handleRowClick={handleSingleRowClick}
              expandedRows={singleExpandedRows}
              sortConfig={singleSortConfig}
              singleTrips={singleTrips}
              pagination={singlePagination}
              onPageChange={setSinglePage}
            />
          </div>

          {/* Section 4: Graph */}
          <div
            className="w-full space-x-2 justify-around flex flex-col rounded-lg h-96 p-2 pr-4 bg-white"
            style={{
              boxShadow:
                "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.06), 0 -4px 6px -1px rgba(0,0,0,0.1), 0 -2px 4px -2px rgba(0,0,0,0.06)",
            }}
          >
            <div className="text-left flex justify-start items-start px-2">
              <p
                className="text-xl flex items-center justify-center font-medium"
                style={{ color: theme_color }}
              >
                Occupancy Rate
              </p>
            </div>
            <OccupancyGraph
              ref={chartRef}
              labels={labels}
              companyData={siteData}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default CompanyAdminDashboardNew;
